import type { LocalizedText } from "@platform/core";
import { Field, Textarea } from "@platform/ui";
import { useT } from "../i18n";

export interface HeroText {
  heroTitle?: LocalizedText;
  heroBody?: LocalizedText;
}

/** Built-in auth-ui text (apps/auth-ui/src/locales hero.*) shown as placeholders — keep in sync. */
export function builtInHero(productName: string): { title: LocalizedText; body: LocalizedText } {
  return {
    title: { th: "เข้าสู่ระบบครั้งเดียว\nใช้ได้ทุกแอป", en: "One sign-in\nfor every app" },
    body: {
      th: `บัญชี ${productName} บัญชีเดียวใช้ได้กับทุกแอปขององค์กร เข้าสู่ระบบด้วยอีเมล เบอร์โทร หรือรหัสผู้ใช้งาน และปกป้องด้วยการยืนยันตัวตนสองขั้นตอน`,
      en: `Your ${productName} account works across all of the organization's applications. Sign in with your email, phone number or user code — protected by two-step verification.`,
    },
  };
}

/** The text visitors see when a field is empty: `base` (e.g. the global branding) per language, else built-in. */
export function heroPlaceholder(productName: string, base?: HeroText) {
  const d = builtInHero(productName);
  return {
    title: { th: base?.heroTitle?.th || d.title.th, en: base?.heroTitle?.en || d.title.en },
    body: { th: base?.heroBody?.th || d.body.th, en: base?.heroBody?.en || d.body.en },
  };
}

/** Drop empty languages / fields so "empty" is stored as absent (= built-in text on the auth pages). */
export function cleanHeroText(v: HeroText): HeroText {
  const clean = (x?: LocalizedText) => {
    const out: LocalizedText = {};
    if (x?.th?.trim()) out.th = x.th.trim();
    if (x?.en?.trim()) out.en = x.en.trim();
    return Object.keys(out).length ? out : undefined;
  };
  const title = clean(v.heroTitle);
  const body = clean(v.heroBody);
  return { ...(title ? { heroTitle: title } : {}), ...(body ? { heroBody: body } : {}) };
}

/**
 * Headline + text of the sign-in pages' brand panel, in Thai and English (docs/branding.md). `placeholder` shows the
 * text used when a field is left empty.
 */
export function HeroTextFields({
  value,
  onChange,
  placeholder,
  errors = {},
}: {
  value: HeroText;
  onChange: (v: HeroText) => void;
  placeholder?: { title: LocalizedText; body: LocalizedText };
  errors?: Record<string, string>;
}) {
  const { t } = useT();
  const set = (key: keyof HeroText, lang: keyof LocalizedText, text: string) =>
    onChange({ ...value, [key]: { ...value[key], [lang]: text } });
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      {(["th", "en"] as const).map((lang) => (
        <div key={lang} className="min-w-0">
          <Field
            label={t(`heroText.title.${lang}`)}
            hint={t("heroText.titleHint")}
            error={errors[`heroTitle.${lang}`]}
          >
            {(p) => (
              <Textarea
                {...p}
                data-testid={`hero-title-${lang}`}
                rows={2}
                className="min-h-0"
                maxLength={120}
                placeholder={placeholder?.title[lang]}
                value={value.heroTitle?.[lang] ?? ""}
                onChange={(e) => set("heroTitle", lang, e.target.value)}
              />
            )}
          </Field>
          <Field label={t(`heroText.body.${lang}`)} error={errors[`heroBody.${lang}`]}>
            {(p) => (
              <Textarea
                {...p}
                data-testid={`hero-body-${lang}`}
                rows={4}
                maxLength={500}
                placeholder={placeholder?.body[lang]}
                value={value.heroBody?.[lang] ?? ""}
                onChange={(e) => set("heroBody", lang, e.target.value)}
              />
            )}
          </Field>
        </div>
      ))}
    </div>
  );
}
